/* Parsing an answer into the blocks it is actually made of.
 *
 * The model writes prose, bullets, ordered steps and — for figures — a small markdown table.
 * Rendering all of that as <p> per line was the old behaviour, and it flattened the one thing
 * a reader needs from a policy answer: its structure. A comparison of two years arrived as a
 * sentence with four numbers in it; a four-step procedure arrived as four paragraphs.
 *
 * Everything here is pure and total: any input produces blocks, and anything unrecognised
 * falls through to a paragraph. A malformed table is prose, never an exception.
 */

export type AnswerBlock =
  | { kind: 'p'; text: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[] }
  | { kind: 'quote'; text: string }
  | { kind: 'table'; head: string[]; rows: string[][] }

const BULLET = /^\s*[-*•]\s+/
const NUMBERED = /^\s*\d+[.)]\s+/
const QUOTE = /^\s*>\s?/
/** A table line: starts and ends with a pipe once trimmed, and has at least two cells. */
const TABLE_ROW = /^\s*\|(.+)\|\s*$/
/** The header underline: only pipes, dashes, colons and spaces. */
const TABLE_RULE = /^\s*\|[\s:|-]+\|\s*$/

function cells(line: string): string[] {
  const inner = TABLE_ROW.exec(line)?.[1] ?? ''
  return inner.split('|').map((c) => c.trim())
}

/** Group an answer into paragraphs, lists, quotes and tables. */
export function parseAnswer(content: string): AnswerBlock[] {
  const blocks: AnswerBlock[] = []
  const lines = content.split('\n')
  let para: string[] = []

  const flushPara = () => {
    if (para.length) {
      blocks.push({ kind: 'p', text: para.join(' ') })
      para = []
    }
  }

  for (let i = 0; i < lines.length; i += 1) {
    const raw = lines[i] ?? ''
    const line = raw.trim()

    if (!line) {
      flushPara()
      continue
    }

    // A table needs its header underline on the very next line; without it the pipes are
    // just punctuation and the line stays prose.
    if (TABLE_ROW.test(line) && TABLE_RULE.test((lines[i + 1] ?? '').trim())) {
      const head = cells(line)
      const rows: string[][] = []
      let j = i + 2
      for (; j < lines.length; j += 1) {
        const next = (lines[j] ?? '').trim()
        if (!TABLE_ROW.test(next)) break
        const row = cells(next)
        // Pad or trim so every row matches the header — a ragged row would otherwise
        // silently shift a figure under the wrong heading.
        while (row.length < head.length) row.push('')
        rows.push(row.slice(0, head.length))
      }
      // A header with no surviving rows is not a table. Verification can empty one by
      // dropping rows whose citation failed, and an empty grid tells the reader nothing.
      if (rows.length > 0) {
        flushPara()
        blocks.push({ kind: 'table', head, rows })
        i = j - 1
        continue
      }
      i = j - 1
      continue
    }

    if (QUOTE.test(line)) {
      flushPara()
      const parts: string[] = [line.replace(QUOTE, '')]
      let j = i + 1
      for (; j < lines.length; j += 1) {
        const next = (lines[j] ?? '').trim()
        if (!QUOTE.test(next)) break
        parts.push(next.replace(QUOTE, ''))
      }
      blocks.push({ kind: 'quote', text: parts.join(' ').trim() })
      i = j - 1
      continue
    }

    const kind: 'ul' | 'ol' | null = BULLET.test(line) ? 'ul' : NUMBERED.test(line) ? 'ol' : null
    if (!kind) {
      // A soft-wrapped continuation belongs to the paragraph above it, not to a new one.
      para.push(line)
      continue
    }

    flushPara()
    const item = line.replace(kind === 'ul' ? BULLET : NUMBERED, '')
    const last = blocks[blocks.length - 1]
    if (last && last.kind === kind) last.items.push(item)
    else blocks.push({ kind, items: [item] })
  }

  flushPara()
  return blocks
}

/* ------------------------------------------------------------------ numbers */

/** Citation markers are not part of a cell's value. */
const MARKER_G = /\[\d+\]/g

/**
 * The scale or unit a figure may carry after it. A financial extract writes "Rs. 28,281.232
 * million" and a leave policy writes "25 days" — both are figures, and refusing them left a
 * table of numbers rendered as text.
 */
const UNIT_SUFFIX =
  /^(million|mn|billion|bn|crore|cr|lakhs?|thousand|days?|working days?|hours?|hrs?|months?|years?)$/i

export interface CellValue {
  value: number
  /** Normalised trailing unit, '' when the figure is bare. Rows must agree on it. */
  unit: string
}

/**
 * The figure a cell states, or null when it does not state one.
 *
 * Accepts the shapes a policy or annual report actually uses: "₹30,000", "Rs. 5,000",
 * "28,281.232 million", "12%", "25 days", "(1,204)" for a negative. Anything else — a date,
 * a range, a sentence — is not a figure, and returning null is what keeps a chart off a
 * table that has none.
 */
export function parseCell(raw: string): CellValue | null {
  const cleaned = raw
    .replace(MARKER_G, '')
    .replace(/\*\*/g, '')
    .replace(/(₹|\$|€|£)/g, '')
    .replace(/\bRs\.?|\bINR\b/gi, '')
    .replace(/,/g, '')
    .trim()
  const parenthesised = /^\((.+)\)$/.exec(cleaned)
  const body = (parenthesised ? parenthesised[1] : cleaned) ?? ''

  const match = /^(-?\d+(?:\.\d+)?)\s*(%|[A-Za-z ]*)$/.exec(body)
  if (!match) return null
  const suffix = (match[2] ?? '').trim()
  if (suffix !== '' && suffix !== '%' && !UNIT_SUFFIX.test(suffix)) return null

  const value = Number.parseFloat(match[1] ?? '')
  if (!Number.isFinite(value)) return null
  return { value: parenthesised ? -value : value, unit: suffix.toLowerCase() }
}

/** The bare number, for callers that only need the magnitude. */
export function cellNumber(raw: string): number | null {
  return parseCell(raw)?.value ?? null
}

/**
 * True when a column reads as figures AND every row states them in the same unit.
 *
 * The shared-unit rule is what keeps a column of "5 days" and "3 months" out of a chart:
 * plotted side by side those bars would compare quantities that are not comparable.
 */
export function isNumericColumn(rows: string[][], index: number): boolean {
  const filled = rows
    .map((r) => r[index] ?? '')
    .filter((v) => v.replace(MARKER_G, '').trim() !== '')
  if (filled.length === 0) return false
  const parsed = filled.map(parseCell)
  if (parsed.some((p) => p === null)) return false
  const units = new Set(parsed.map((p) => p?.unit ?? ''))
  return units.size === 1
}

export type ChartUnit = 'currency' | 'percent' | 'plain'

export interface ChartableTable {
  labels: string[]
  series: { name: string; values: number[] }[]
  unit: ChartUnit
  /** "Rs. million", "%" — stated once on the chart instead of on every mark. */
  unitLabel: string
  /** Values as the table wrote them, for direct labels — never reformatted arithmetic. */
  display: string[][]
}

/**
 * The chart a table can support, or null.
 *
 * A chart here is a VIEW OF THE TABLE, nothing more: every value it plots is a value the
 * table already shows and the answer already cited. It is offered only when the reading is
 * unambiguous — a label column, between two and twelve rows, and one to three columns that
 * are numeric all the way down. Anything else renders as the table alone, which is the
 * correct outcome rather than a fallback.
 */
export function chartableFrom(table: { head: string[]; rows: string[][] }): ChartableTable | null {
  const { head, rows } = table
  if (head.length < 2 || rows.length < 2 || rows.length > 12) return null

  const numericIdx: number[] = []
  for (let c = 1; c < head.length; c += 1) {
    if (isNumericColumn(rows, c)) numericIdx.push(c)
  }
  if (numericIdx.length === 0 || numericIdx.length > 3) return null

  const labels = rows.map((r) => (r[0] ?? '').replace(MARKER_G, '').replace(/\*\*/g, '').trim())
  if (labels.some((l) => l === '')) return null

  const series = numericIdx.map((c) => ({
    name: (head[c] ?? '').replace(/\*\*/g, '').trim() || `Series ${c}`,
    values: rows.map((r) => cellNumber(r[c] ?? '') ?? 0),
  }))

  // A bar chart starting at zero cannot show a set that is entirely zero, and a table of
  // zeroes is more likely a parse artefact than a finding.
  if (series.every((s) => s.values.every((v) => v === 0))) return null

  const rawNumeric = rows.flatMap((r) => numericIdx.map((c) => r[c] ?? ''))
  const unit: ChartUnit = rawNumeric.some((v) => /(₹|\bRs\.?|\bINR\b|\$)/i.test(v))
    ? 'currency'
    : rawNumeric.some((v) => v.includes('%'))
      ? 'percent'
      : 'plain'

  // The unit is stated once, on the chart, rather than repeated on every bar.
  const scale = parseCell(rows[0]?.[numericIdx[0] ?? 1] ?? '')?.unit ?? ''
  const currencyWord = unit === 'currency' ? 'Rs.' : ''
  const unitLabel = [currencyWord, scale === '%' ? '' : scale].filter(Boolean).join(' ').trim()

  return {
    labels,
    series,
    unit,
    unitLabel: unitLabel || (unit === 'percent' ? '%' : ''),
    display: rows.map((r) => numericIdx.map((c) => (r[c] ?? '').replace(MARKER_G, '').trim())),
  }
}
